<template>
  <div :class="['min-h-screen transition-colors duration-1000 selection:bg-amber-800 selection:text-amber-50 paper-texture', themeClass]">
    <header :class="['sticky top-0 z-50 border-b-4 transition-colors duration-1000 p-4 sepia-shadow', headerClass]">
      <div class="container mx-auto flex justify-between items-center">
        <h1 class="text-3xl font-serif-display cursor-pointer hover:opacity-80 transition-opacity text-shadow-warm" @click="view = 'landing'" style="letter-spacing: 0.05em;">
          <span :class="titleClass">Collected</span> <span :class="accentClass">Wit</span>
        </h1>
        <div class="flex items-center space-x-6">
          <Mascot v-if="view !== 'over'" size="w-12 h-12 md:w-16 md:h-16" :mood="mascotMood" />
          <span v-if="view === 'game'" class="parchment-bg px-4 py-1 font-serif-body text-xl sepia-shadow border-2 border-amber-900/40 ink-text" style="border-radius: 25px;">{{ score }} pts</span>
        </div>
      </div>
    </header>

    <main class="container mx-auto p-4 md:p-8">
      <LandingPage v-if="view === 'landing'" @start-game="handleStartGame" @update-mode="m => selectedMode = m"/>
      <GamePlay
        v-if="view === 'game'"
        :player-name="playerName"
        :game-mode="gameMode"
        :initial-passes="initialPasses"
        @game-over="endGame"
        @update-score="handleScoreUpdate"
      />
      <GameOver
        v-if="view === 'over'"
        :stats="gameStats"
        :game-mode="gameMode"
        @restart="resetGame"
        @show-ad="handleShowAd"
      />
    </main>

    <!-- Pre-Game Ad Offer Modal -->
    <PreGameOffer
        :visible="showPreGameOffer"
        :is-easy="selectedMode === 'easy'"
        @watch-ad="handleWatchPreGameAd"
        @skip="handleSkipPreGameOffer"
    />

    <!-- Ad Display Modal -->
    <AdModal
        :visible="showAdModal"
        :is-easy="adModalIsEasy"
        :ad-config="adConfig"
        @skip="handleAdComplete"
        @complete="handleAdComplete"
        @close="handleAdClose"
    />
  </div>
</template>

<script setup>
import {computed, ref} from 'vue'
import {useHead} from '@unhead/vue'
import LandingPage from './LandingPage.vue'
import GamePlay from './GamePlay.vue'
import GameOver from './GameOver.vue'
import Mascot from './Mascot.vue'
import PreGameOffer from './PreGameOffer.vue'
import AdModal from './AdModal.vue'
import {trackRiddleGame} from '../analytics'

const view = ref('landing')

const pageTitle = computed(() => {
  switch (view.value) {
    case 'landing': return 'Welcome'
    case 'game': return 'Playing'
    case 'over': return 'Game Over'
    default: return 'Home'
  }
})

const score = ref(0)
const gameStats = ref(null)
const playerName = ref('')
const gameMode = ref('blitz')
const selectedMode = ref('blitz')
const mascotMood = ref('neutral')

// Ad-related state
const showPreGameOffer = ref(false)
const showAdModal = ref(false)
const adModalIsEasy = ref(false)
const adConfig = ref({})
const initialPasses = ref(1) // Base passes (config MAX_PASSES)
const pendingGameStart = ref(null) // Stores {name, mode} while showing pre-game offer
const pendingAdCallback = ref(null) // Callback for ad completion (e.g., reveal riddle)

const themeClass = computed(() => {
  if (view.value === 'landing') {
    return selectedMode.value === 'easy'
      ? 'parchment-bg ink-text'
      : 'slate-texture text-amber-50'
  }
  if (view.value === 'game') {
    return gameMode.value === 'easy'
      ? 'parchment-bg ink-text'
      : 'slate-texture text-amber-50'
  }
  return 'slate-texture text-amber-50'
})

const headerClass = computed(() => {
  const isEasy = (view.value === 'landing' && selectedMode.value === 'easy') ||
                 (view.value === 'game' && gameMode.value === 'easy')

  return isEasy
    ? 'bg-amber-100/95 border-amber-700/50'
    : 'bg-slate-800/95 border-slate-600/70'
})

const titleClass = computed(() => {
  const isEasy = (view.value === 'landing' && selectedMode.value === 'easy') ||
                 (view.value === 'game' && gameMode.value === 'easy')
  return isEasy ? 'text-amber-900' : 'text-amber-200'
})

const accentClass = computed(() => {
  const isEasy = (view.value === 'landing' && selectedMode.value === 'easy') ||
                 (view.value === 'game' && gameMode.value === 'easy')
  return isEasy ? 'text-amber-700' : 'text-amber-500'
})

// Handle start game from landing page - shows pre-game offer first
const handleStartGame = (name, mode) => {
  pendingGameStart.value = {name, mode}
  showPreGameOffer.value = true
}

// User chose to watch ad for bonus passes
const handleWatchPreGameAd = () => {
  showPreGameOffer.value = false
  adModalIsEasy.value = selectedMode.value === 'easy'
  adConfig.value = {
    type: 'rewarded',
    rewardType: 'bonus_passes'
  }
  pendingAdCallback.value = () => {
    // Ad completed, give bonus passes
    initialPasses.value = 3 // 1 base + 2 bonus
    actuallyStartGame()
  }
  showAdModal.value = true
}

// User skipped the pre-game offer
const handleSkipPreGameOffer = () => {
  showPreGameOffer.value = false
  initialPasses.value = 1 // Base passes only
  actuallyStartGame()
}

// Actually start the game after ad decision
const actuallyStartGame = () => {
  if (!pendingGameStart.value) return

  const {name, mode} = pendingGameStart.value
  playerName.value = name
  gameMode.value = mode
  score.value = 0
  view.value = 'game'
  mascotMood.value = 'thinking'
  pendingGameStart.value = null

  // Track game start
  trackRiddleGame.start(mode)
}

// Handle ad request from GameOver (for revealing passed riddles)
const handleShowAd = (callback) => {
  adModalIsEasy.value = gameMode.value === 'easy'
  adConfig.value = {
    type: 'standard'
  }
  pendingAdCallback.value = callback
  showAdModal.value = true
}

// Ad modal completed (skipped after watching)
const handleAdComplete = () => {
  showAdModal.value = false
  if (pendingAdCallback.value) {
    pendingAdCallback.value()
    pendingAdCallback.value = null
  }
}

// Ad modal closed without completion
const handleAdClose = () => {
  showAdModal.value = false
  pendingAdCallback.value = null
}

const handleScoreUpdate = (newScore) => {
  if (newScore > score.value) {
    mascotMood.value = 'happy'
    setTimeout(() => { mascotMood.value = 'thinking' }, 2000)
  }
  score.value = newScore
}

const endGame = (stats) => {
  gameStats.value = stats
  view.value = 'over'
  mascotMood.value = 'neutral'

  // Track game completion
  trackRiddleGame.complete(stats)
}

const resetGame = () => {
  view.value = 'landing'
  score.value = 0
  initialPasses.value = 1 // Reset to base passes
}

useHead({
  title: pageTitle,
  titleTemplate: '%s | Collected Wit',
  meta: [
    { name: 'description', content: 'A fun riddle game for all ages - children, teens, and adults' },
  ]
})

</script>
